import { AudioLines, Box, Captions, ChevronRight, Eye, EyeOff, Film, Image, KeyRound, Lock, LockOpen, SlidersHorizontal, Type, Volume2, VolumeX } from 'lucide-react';
import { useEffect, useState, type PointerEvent } from 'react';
import type { Composition, Layer, Project } from '../../core/schema';
import { collectSnapTargets, trimLayer } from '../../core/timeline';
import { snapTime } from '../../core/snapping';
import { useEditorStore } from '../../store/editorStore';
import { usePlaybackStore } from '../../store/playbackStore';
import { getWaveform } from '../../services/waveformService';

const typeIcons = { video: Film, audio: AudioLines, image: Image, text: Type, shape: Box, caption: Captions, scene3d: Box, adjustment: SlidersHorizontal, null: KeyRound, group: ChevronRight };

function clipColor(type: Layer['type']) { return `clip-type-${type}`; }

export function TrackRow({ layer, project, composition, zoom, labelWidth, index }: { layer: Layer; project: Project; composition: Composition; zoom: number; labelWidth: number; index: number }) {
  const selected = useEditorStore((state) => state.selectedLayerIds.includes(layer.id));
  const selectedIds = useEditorStore((state) => state.selectedLayerIds);
  const select = useEditorStore((state) => state.selectLayer);
  const update = useEditorStore((state) => state.updateLayer);
  const beginTransaction = useEditorStore((state) => state.beginTransaction);
  const endTransaction = useEditorStore((state) => state.endTransaction);
  const openComposition = useEditorStore((state) => state.openComposition);
  const snapping = useEditorStore((state) => state.snapping);
  const Icon = typeIcons[layer.type];
  const [waveform, setWaveform] = useState<number[]>([]);
  useEffect(() => {
    if (layer.type !== 'audio' || !layer.assetId) return;
    let active = true;
    void getWaveform(project.id, layer.assetId).then((peaks) => {
      if (!active || !peaks) return;
      const bars = Math.min(80, Math.max(8, Math.round(layer.duration * 10)));
      setWaveform(Array.from({ length: bars }, (_, index) => {
        const peakIndex = Math.floor(index / bars * (peaks.length / 2)) * 2;
        return Math.min(1, Math.max(Math.abs(peaks[peakIndex] ?? 0), Math.abs(peaks[peakIndex + 1] ?? 0)));
      }));
    });
    return () => { active = false; };
  }, [layer.assetId, layer.duration, layer.type, project.id]);

  const beginMove = (event: PointerEvent<HTMLDivElement>) => {
    if (layer.locked || (event.target as HTMLElement).closest('.trim-handle')) return;
    event.stopPropagation();
    const additive = event.metaKey || event.ctrlKey;
    if (!selected || additive) select(layer.id, additive);
    beginTransaction();
    const startX = event.clientX;
    const ids = selected ? selectedIds : [layer.id];
    const starts = Object.fromEntries(ids.map((id) => [id, project.layers[id]?.start ?? 0]));
    const targets = collectSnapTargets(project, composition, ids);
    const move = (moveEvent: globalThis.PointerEvent) => {
      let delta = (moveEvent.clientX - startX) / zoom;
      if (snapping) {
        const source = starts[layer.id] + delta;
        delta += snapTime(source, [...targets, { time: usePlaybackStore.getState().currentTime, type: 'playhead' }], 9 / zoom, composition.fps).delta;
      }
      for (const id of ids) update(id, { start: Math.max(0, starts[id] + delta) }, false);
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); endTransaction(); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up, { once: true });
  };

  const beginTrim = (event: PointerEvent, edge: 'start' | 'end') => {
    event.stopPropagation();
    if (layer.locked) return;
    select(layer.id);
    beginTransaction();
    const startX = event.clientX;
    const original = structuredClone(layer);
    const base = edge === 'start' ? layer.start : layer.start + layer.duration;
    const targets = collectSnapTargets(project, composition, [layer.id]);
    const move = (moveEvent: globalThis.PointerEvent) => {
      let time = base + (moveEvent.clientX - startX) / zoom;
      if (snapping) time = snapTime(time, [...targets, { time: usePlaybackStore.getState().currentTime, type: 'playhead' }], 9 / zoom, composition.fps).time;
      update(layer.id, (draft) => Object.assign(draft, trimLayer(original, edge, Math.max(0, time))), false);
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); endTransaction(); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up, { once: true });
  };

  const waveformBars = layer.type === 'audio' ? (waveform.length ? waveform : Array.from({ length: Math.min(80, Math.max(8, Math.round(layer.duration * 10))) }, (_, index) => 0.25 + ((index * 17) % 70) / 100)) : [];
  return <div className={`track-row ${selected ? 'is-selected' : ''}`} style={{ top: index * 38 }}>
    <div className="track-label" style={{ width: labelWidth }} onClick={(event) => select(layer.id, event.metaKey || event.ctrlKey)}>
      <button className="track-disclosure"><ChevronRight size={11} /></button><span className={`track-icon ${clipColor(layer.type)}`}><Icon size={12} /></span><span className="track-name">{layer.name}</span>
      {(layer.type === 'audio' || layer.type === 'video') && <button onClick={(event) => { event.stopPropagation(); update(layer.id, { muted: !layer.muted }); }}>{layer.muted ? <VolumeX size={11} /> : <Volume2 size={11} />}</button>}
      <button onClick={(event) => { event.stopPropagation(); update(layer.id, { visible: !layer.visible }); }}>{layer.visible ? <Eye size={11} /> : <EyeOff size={11} />}</button>
      <button onClick={(event) => { event.stopPropagation(); update(layer.id, { locked: !layer.locked }); }}>{layer.locked ? <Lock size={10} /> : <LockOpen size={10} />}</button>
    </div>
    <div className="track-lane" style={{ left: labelWidth }} onClick={() => select(layer.id)}>
      <div className={`timeline-clip ${clipColor(layer.type)} ${selected ? 'is-selected' : ''} ${layer.muted ? 'is-muted' : ''}`} style={{ left: layer.start * zoom, width: Math.max(14, layer.duration * zoom) }} onPointerDown={beginMove} onDoubleClick={() => { if (layer.nestedCompositionId) openComposition(layer.nestedCompositionId); }} data-layer-id={layer.id}>
        <div className="clip-content"><Icon size={11} /><b>{layer.name}</b>{layer.speed !== 1 && <span>{Math.round(layer.speed * 100)}%</span>}</div>
        {layer.type === 'video' && <div className="filmstrip">{Array.from({ length: Math.min(12, Math.max(1, Math.round(layer.duration * zoom / 70))) }).map((_, i) => <i key={i} />)}</div>}
        {layer.type === 'audio' && <div className="waveform">{waveformBars.map((amplitude, bar) => <i key={bar} style={{ height: `${Math.max(8, amplitude * 100)}%` }} />)}</div>}
        {Object.values(layer.keyframes).flat().map((keyframe) => <span className="clip-keyframe" key={keyframe.id} style={{ left: Math.min(layer.duration, keyframe.time) * zoom }} />)}
        <button className="trim-handle trim-start" onPointerDown={(event) => beginTrim(event, 'start')} /><button className="trim-handle trim-end" onPointerDown={(event) => beginTrim(event, 'end')} />
      </div>
    </div>
  </div>;
}
